import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';

type Pointer = Pick<MouseEvent, 'clientX' | 'clientY'>;

type Bounds = Pick<DOMRect, 'top' | 'right' | 'bottom' | 'left'>;

export type ListRow = {
  node: DataRoomNode;
  element: HTMLElement;
  top: number;
  bottom: number;
};

export const isPointerInsideContainer = (e: Pointer, container: DOMRect) => {
  if (!container) return false;

  const { clientX, clientY } = e;

  return (
    clientX > container.x &&
    clientX < container.x + container.width &&
    clientY > container.y &&
    clientY < container.y + container.height
  );
};

const getClamped = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

// Positions are relative to the list, so they stay valid while the page scrolls.
export const measureRows = (
  list: HTMLElement,
  nodes: DataRoomNode[],
): ListRow[] => {
  const nodesById = new Map(nodes.map((node) => [node._id, node]));
  const { top: listTop } = list.getBoundingClientRect();

  return Array.from(
    list.querySelectorAll<HTMLElement>(':scope > [data-node-id]'),
  )
    .map((element) => {
      const { top, bottom } = element.getBoundingClientRect();

      return {
        node: nodesById.get(element.dataset.nodeId),
        element,
        top: top - listTop,
        bottom: bottom - listTop,
      };
    })
    .filter(({ node }) => !!node);
};

export const getRowAtY = (rows: ListRow[], y: number) =>
  rows.find(({ top, bottom }) => y >= top && y < bottom);

export const getRowsInBand = (
  rows: ListRow[],
  startY: number,
  endY: number,
) => {
  const bandTop = Math.min(startY, endY);
  const bandBottom = Math.max(startY, endY);

  return rows.filter(
    ({ top, bottom }) => bottom > bandTop && top <= bandBottom,
  );
};

// The document element's rect spans the whole page, so it is cut to the viewport.
export const getVisibleBounds = (element: HTMLElement): Bounds => {
  const { top, right, bottom, left } = element.getBoundingClientRect();

  return {
    top: Math.max(top, 0),
    right: Math.min(right, window.innerWidth),
    bottom: Math.min(bottom, window.innerHeight),
    left: Math.max(left, 0),
  };
};

export const getIntersection = (a: Bounds, b: Bounds): Bounds => ({
  top: Math.max(a.top, b.top),
  right: Math.min(a.right, b.right),
  bottom: Math.min(a.bottom, b.bottom),
  left: Math.max(a.left, b.left),
});

export const getAutoScrollDirection = (
  clientY: number,
  { top, bottom }: Bounds,
  edge: number,
) => {
  if (clientY < top + edge) return -1;

  if (clientY > bottom - edge) return 1;

  return 0;
};

// The rectangle between two points, clamped to `bounds`.
export const getSelectionRectStyle = (
  start: Pointer,
  end: Pointer,
  bounds: Bounds,
) => {
  const left = getClamped(
    Math.min(start.clientX, end.clientX),
    bounds.left,
    bounds.right,
  );
  const right = getClamped(
    Math.max(start.clientX, end.clientX),
    bounds.left,
    bounds.right,
  );
  const top = getClamped(
    Math.min(start.clientY, end.clientY),
    bounds.top,
    bounds.bottom,
  );
  const bottom = getClamped(
    Math.max(start.clientY, end.clientY),
    bounds.top,
    bounds.bottom,
  );

  if (right <= left || bottom <= top) return 'display:none;';

  return `display:block; top:${top}px; left:${left}px; width:${
    right - left
  }px; height:${bottom - top}px;`;
};
